import React, {
  ComponentProps,
  Fragment,
  PropsWithChildren,
  RefObject,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  FlatList,
  ScrollView,
  SectionList,
  StyleSheet,
  View,
} from 'react-native';
import { useTheme } from '@ui-kitten/components';
import lodashIsEmpty from 'lodash/isEmpty';
import Tooltip from 'rn-tooltip';

type OtherToolTipProps = Partial<
  Omit<ComponentProps<typeof Tooltip>, 'actionType'>
>;

type ScrollTo = Parameters<ScrollView['scrollTo']>[0];
type ScrollToOffset = Parameters<FlatList<any>['scrollToOffset']>[0];
type ScrollToLocation = Parameters<
  SectionList<any, any>['scrollToLocation']
>[0];

export type HintProps = PropsWithChildren &
  OtherToolTipProps & {
    backgroundColor?: string;
    show: boolean;
    tip: React.ReactElement;
    scrollProps?: ScrollTo | ScrollToOffset | ScrollToLocation;
    scrollRef?: RefObject<ScrollView | FlatList | SectionList>;
  };

const Hint = ({
  backgroundColor,
  children,
  scrollProps,
  scrollRef,
  show,
  tip,
  ...rest
}: HintProps) => {
  const theme = useTheme();
  const tooltipRef = useRef<Tooltip>();
  const showTipTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (show) {
      let isScrolling = false;
      if (
        !lodashIsEmpty(scrollProps) &&
        scrollRef?.current &&
        typeof scrollProps === 'object'
      ) {
        if ('scrollTo' in scrollRef.current) {
          // for ScrollView
          isScrolling = true;
          scrollRef.current.scrollTo({
            ...scrollProps,
            animated: true,
          } as ScrollTo);
        } else if ('scrollToOffset' in scrollRef.current) {
          isScrolling = true;
          scrollRef.current.scrollToOffset({
            ...scrollProps,
            animated: true,
          } as ScrollToOffset);
        } else if ('scrollToLocation' in scrollRef.current) {
          isScrolling = true;
          // for SectionList
          scrollRef.current.scrollToLocation({
            ...scrollProps,
            animated: true,
          } as ScrollToLocation);
        }
      }
      clearTimeout(showTipTimeout.current);
      showTipTimeout.current = setTimeout(
        () => {
          tooltipRef.current?.toggleTooltip();
        },
        isScrolling ? 750 : 500,
      );
    }
    return () => {
      clearTimeout(showTipTimeout.current);
    };
  }, [scrollProps, scrollRef, show]);

  const bgColor = useMemo(
    () => backgroundColor || theme['color-success-500'],
    [backgroundColor, theme],
  );

  return (
    <Tooltip
      ref={tooltipRef}
      actionType="none"
      backgroundColor={bgColor}
      overlayColor="#80808096"
      height={null}
      width={null}
      popover={tip}
      {...rest}
    >
      <Fragment>
        {/* show invisible element above children to prevent clicking inside */}
        {show && <View style={styles.highlightOverlay} />}
        {children}
      </Fragment>
    </Tooltip>
  );
};

const styles = StyleSheet.create({
  highlightOverlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1,
  },
});

export default Hint;
